import { supabase } from "@/lib/supabaseClient";

export type WhatsAppStatus = {
  linked: boolean;
  optedIn: boolean;
  phoneLast4: string | null;
  /** Número de WhatsApp del negocio (solo dígitos, con código de país). */
  number: string | null;
};

const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

export async function fetchWhatsAppStatus(): Promise<WhatsAppStatus> {
  const { data, error } = await supabase.rpc("wa_status");
  if (error) throw error;
  const d = (data ?? {}) as Partial<WhatsAppStatus>;
  return { linked: !!d.linked, optedIn: !!d.optedIn, phoneLast4: d.phoneLast4 ?? null, number: d.number ?? null };
}

/** Genera un código de activación y devuelve el enlace de WhatsApp listo para abrir. */
export async function startWhatsAppActivation(): Promise<{ url: string; code: string } | null> {
  const { data, error } = await supabase.rpc("wa_start", { p_tz: tz() });
  if (error) throw error;
  const d = data as { code: string | null; number: string | null; optedIn: boolean } | null;
  if (!d || !d.code || !d.number) return null;
  const text = `Hola Vívelo AI, quiero activar mis avisos por WhatsApp. Mi código: ${d.code}`;
  return { url: `https://wa.me/${d.number}?text=${encodeURIComponent(text)}`, code: d.code };
}

export async function disableWhatsApp(): Promise<void> {
  const { error } = await supabase.rpc("wa_disable");
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export type NotificationSettings = {
  whatsappNumber: string;
  remind24h: boolean;
  remind1h: boolean;
  template24h: string;
  template1h: string;
  templateCancel: string;
};

export async function fetchNotificationSettings(): Promise<NotificationSettings | null> {
  const { data, error } = await supabase.from("notification_settings").select("*").maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    whatsappNumber: (data.whatsapp_number as string | null) ?? "",
    remind24h: !!data.remind_24h,
    remind1h: !!data.remind_1h,
    template24h: data.template_24h as string,
    template1h: data.template_1h as string,
    templateCancel: data.template_cancel as string,
  };
}

export async function saveNotificationSettings(s: NotificationSettings): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) return;
  const { data: prof } = await supabase.from("profiles").select("organization_id").eq("id", auth.user.id).maybeSingle();
  if (!prof?.organization_id) return;
  const { error } = await supabase
    .from("notification_settings")
    .update({
      whatsapp_number: s.whatsappNumber.trim() || null,
      remind_24h: s.remind24h,
      remind_1h: s.remind1h,
      template_24h: s.template24h,
      template_1h: s.template1h,
      template_cancel: s.templateCancel,
      updated_at: new Date().toISOString(),
    })
    .eq("organization_id", prof.organization_id);
  if (error) throw error;
}

export type OutboxLogRow = {
  id: string;
  kind: string;
  status: "pending" | "sending" | "sent" | "failed";
  error: string | null;
  created_at: string;
  sent_at: string | null;
  user_name: string | null;
  phone_last4: string;
  event_title: string | null;
};

export type NotificationsOverview = { linked: number; pending: number; sent7d: number; failed7d: number; log: OutboxLogRow[] };

export async function fetchNotificationsOverview(): Promise<NotificationsOverview | null> {
  const { data, error } = await supabase.rpc("notifications_admin_overview");
  if (error) throw error;
  return (data as NotificationsOverview | null) ?? null;
}
